import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface TagInstanceNavigationTag {
  tagId: unknown;
  normalizedValue: string;
}

interface TagInstanceNavigationDetection {
  _id: unknown;
  normalizedText: string;
  pageNumber: number;
}

interface UseTagInstanceNavigationArgs<
  TTag extends TagInstanceNavigationTag,
  TDetection extends TagInstanceNavigationDetection,
> {
  tags: TTag[];
  activeDetections: TDetection[];
  enabled: boolean;
  navigationScopeKey: string;
  onFocusDetection: (detection: TDetection) => void;
}

export function useTagInstanceNavigation<
  TTag extends TagInstanceNavigationTag,
  TDetection extends TagInstanceNavigationDetection,
>({
  tags,
  activeDetections,
  enabled,
  navigationScopeKey,
  onFocusDetection,
}: UseTagInstanceNavigationArgs<TTag, TDetection>) {
  const [focusedDetectionIdByTagId, setFocusedDetectionIdByTagId] =
    useState<Record<string, string>>({});
  const [highlightedDetectionId, setHighlightedDetectionId] = useState<string | null>(null);
  const focusedDetectionIdByTagIdRef = useRef<Record<string, string>>({});

  const activeDetectionsByTagId = useMemo(() => {
    const detectionsByNormalizedText = new Map<string, TDetection[]>();
    for (const detection of activeDetections) {
      const existing = detectionsByNormalizedText.get(detection.normalizedText) ?? [];
      existing.push(detection);
      detectionsByNormalizedText.set(detection.normalizedText, existing);
    }

    for (const detections of detectionsByNormalizedText.values()) {
      detections.sort((left, right) => left.pageNumber - right.pageNumber);
    }

    return new Map(
      tags.map((tag) => [
        String(tag.tagId),
        detectionsByNormalizedText.get(tag.normalizedValue) ?? [],
      ] as const)
    );
  }, [activeDetections, tags]);

  const navigableTagIds = useMemo(
    () =>
      enabled
        ? tags
            .filter((tag) => (activeDetectionsByTagId.get(String(tag.tagId))?.length ?? 0) > 1)
            .map((tag) => String(tag.tagId))
        : [],
    [activeDetectionsByTagId, enabled, tags]
  );

  const navigateTagInstance = useCallback(
    (tagId: unknown, direction: 1 | -1) => {
      if (!enabled) {
        return;
      }

      const tagKey = String(tagId);
      const detections = activeDetectionsByTagId.get(tagKey) ?? [];
      if (detections.length < 2) {
        return;
      }

      const currentDetectionId = focusedDetectionIdByTagIdRef.current[tagKey];
      const currentIndex = currentDetectionId
        ? detections.findIndex((detection) => String(detection._id) === currentDetectionId)
        : -1;
      const baseIndex =
        currentIndex === -1 ? (direction === 1 ? -1 : 0) : currentIndex;
      const nextDetection =
        detections[(baseIndex + direction + detections.length) % detections.length];
      if (!nextDetection) {
        return;
      }

      const nextDetectionId = String(nextDetection._id);
      onFocusDetection(nextDetection);
      setHighlightedDetectionId(nextDetectionId);
      focusedDetectionIdByTagIdRef.current = {
        ...focusedDetectionIdByTagIdRef.current,
        [tagKey]: nextDetectionId,
      };
      setFocusedDetectionIdByTagId((current) => ({
        ...current,
        [tagKey]: nextDetectionId,
      }));
    },
    [activeDetectionsByTagId, enabled, onFocusDetection]
  );

  const focusNextInstanceForTag = useCallback(
    (tagId: unknown) => {
      navigateTagInstance(tagId, 1);
    },
    [navigateTagInstance]
  );

  const focusPreviousInstanceForTag = useCallback(
    (tagId: unknown) => {
      navigateTagInstance(tagId, -1);
    },
    [navigateTagInstance]
  );

  useEffect(() => {
    focusedDetectionIdByTagIdRef.current = focusedDetectionIdByTagId;
  }, [focusedDetectionIdByTagId]);

  useEffect(() => {
    setHighlightedDetectionId(null);
    focusedDetectionIdByTagIdRef.current = {};
    setFocusedDetectionIdByTagId({});
  }, [navigationScopeKey]);

  useEffect(() => {
    const visibleTagIds = new Set(tags.map((tag) => String(tag.tagId)));
    const visibleDetectionIds = new Set(
      activeDetections.map((detection) => String(detection._id))
    );

    setFocusedDetectionIdByTagId((current) => {
      let hasChanges = false;
      const next: Record<string, string> = {};

      for (const [tagId, detectionId] of Object.entries(current)) {
        if (!visibleTagIds.has(tagId)) {
          hasChanges = true;
          continue;
        }

        const detections = activeDetectionsByTagId.get(tagId) ?? [];
        if (!detections.some((detection) => String(detection._id) === detectionId)) {
          hasChanges = true;
          continue;
        }

        next[tagId] = detectionId;
      }

      return hasChanges ? next : current;
    });

    setHighlightedDetectionId((current) =>
      current && visibleDetectionIds.has(current) ? current : null
    );
  }, [activeDetections, activeDetectionsByTagId, tags]);

  return {
    focusNextInstanceForTag,
    focusPreviousInstanceForTag,
    highlightedDetectionId,
    navigableTagIds,
  };
}
